import { useEffect, useCallback } from 'react';
import {
  Stack,
  Group,
  Title,
  Text,
  Button,
  Loader,
  Alert,
  Box,
  Badge,
  Anchor,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useParams } from '@tanstack/react-router';
import { IconAlertCircle, IconArrowLeft } from '@tabler/icons-react';
import { MediaStateTypeId } from '@lib2/model/media/MediaStateTypeObj';
import { ListlistTypeId } from '@lib2/model/listlist/ListlistType';
import { useAppContentListlist } from '../useAppContentListlist';
import { useAppContentListlists } from '../useAppContentListlists';
import { useAppContentPositions } from '../useAppContentPositions';
import { useUpdateListlist } from '../useAppContentMutations';
import { AppContentUtils, type ListlistFormData } from '../AppContentUtils';
import { listlistFormSchema } from '../schemas/ListlistForm.schema';
import { ListlistSettingsSection } from './ListlistSettingsSection';
import { OrderedChildrenEditor } from './OrderedChildrenEditor';
import { FormSaveBar } from '@/shared/components/FormSaveBar';

export function ContentGroupDetailPage() {
  const { id: displayId } = useParams({ strict: false });
  const navigate = useNavigate();

  // Parse base-converted ID
  let llId = 0;
  let idError = false;
  try {
    llId = AppContentUtils.getNumericId(displayId!);
    if (!llId || llId <= 0) idError = true;
  } catch {
    idError = true;
  }

  const { data: listlist, isLoading, isError, error } = useAppContentListlist(llId);
  const { categories } = useAppContentListlists();
  const { data: positions } = useAppContentPositions();
  const updateMutation = useUpdateListlist(llId);

  const llr = listlist?.llr ?? null;
  const isActive = llr ? llr.mediaStateTypeId === MediaStateTypeId.Active : false;

  // Form for settings
  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<ListlistFormData>({
    resolver: zodResolver(listlistFormSchema),
    defaultValues: {
      title: '',
      descShort: '',
      mediaStateTypeId: MediaStateTypeId.Active,
    },
  });

  // Sync form when data loads
  useEffect(() => {
    if (llr) {
      reset(AppContentUtils.transformToFormData(llr));
    }
  }, [llr, reset]);

  const handleSave = handleSubmit((data: ListlistFormData) => {
    const req = AppContentUtils.buildUpdateReq(data);
    updateMutation.mutate(req, {
      onSuccess: () => {
        // Clean baseline immediately — the refetch-driven effect reset arrives
        // later; without this the save bar stays up for the whole round-trip.
        reset(data);
        notifications.show({
          title: 'Saved',
          message: 'Content group settings updated.',
          color: 'green',
        });
      },
      onError: (err) => {
        notifications.show({ title: 'Error', message: err.message, color: 'red' });
      },
    });
  });

  const handleDiscard = useCallback(() => {
    if (llr) {
      reset(AppContentUtils.transformToFormData(llr));
    }
  }, [llr, reset]);

  // Get ordered category children from positions
  const orderedChildren = positions
    ? AppContentUtils.getOrderedChildren(
        llId,
        [...(positions.rootNodes ?? [])],
      )
    : null;

  const orderedCategoryIds = orderedChildren?.llIds ?? [];

  // Build lookup for category details
  const categoryLookup = new Map(
    categories.map((c) => [c.llr.llId, c]),
  );

  // ── Error states ──

  if (idError) {
    return (
      <Stack>
        <Anchor
          component="button"
          size="sm"
          c="dimmed"
          onClick={() => navigate({ to: '/apps/content', search: { tab: 'groups' } })}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}
        >
          <IconArrowLeft size={14} />
          Back to Content
        </Anchor>
        <Alert icon={<IconAlertCircle size={16} />} color="red">
          Invalid content group ID.
        </Alert>
      </Stack>
    );
  }

  if (isLoading) {
    return (
      <Stack align="center" justify="center" h={400}>
        <Loader size="lg" />
        <Text c="dimmed">Loading content group...</Text>
      </Stack>
    );
  }

  if (isError || !llr) {
    return (
      <Stack>
        <Anchor
          component="button"
          size="sm"
          c="dimmed"
          onClick={() => navigate({ to: '/apps/content', search: { tab: 'groups' } })}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}
        >
          <IconArrowLeft size={14} />
          Back to Content
        </Anchor>
        <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
          {error?.message ?? 'Failed to load content group'}
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      {/* Back link */}
      <Box>
        <Anchor
          component="button"
          size="sm"
          c="dimmed"
          onClick={() => navigate({ to: '/apps/content', search: { tab: 'groups' } })}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}
        >
          <IconArrowLeft size={14} />
          Back to Content
        </Anchor>
      </Box>

      {/* Header */}
      <Group justify="space-between" align="flex-start">
        <Box>
          <Title order={2}>{llr.title}</Title>
          <Group gap="xs" mt={4}>
            <Text size="sm" c="dimmed" ff="monospace">
              {AppContentUtils.getDisplayId(llr.llId)}
            </Text>
            <Badge variant="light" color={isActive ? 'green' : 'gray'} size="sm">
              {isActive ? 'Active' : 'Inactive'}
            </Badge>
          </Group>
        </Box>
      </Group>

      {/* Settings Form */}
      <form onSubmit={handleSave}>
        <Text fw={600} size="lg">
          Settings
        </Text>
        <FormSaveBar
          isDirty={isDirty}
          isPending={updateMutation.isPending}
          onDiscard={handleDiscard}
        />

        <Box mt="md">
          <ListlistSettingsSection control={control} />
        </Box>
      </form>

      {/* Ordered Categories Editor */}
      <Box mt="xl">
        <OrderedChildrenEditor
          parentLlId={llId}
          parentType={ListlistTypeId.Root}
          orderedChildLlIds={orderedCategoryIds}
          allAvailableChildren={categories}
          childLookup={categoryLookup}
          childDetailRoute="/apps/content/categories"
        />
      </Box>
    </Stack>
  );
}
